import { Box, Flex, StyleProps } from "@chakra-ui/react";
import React from "react";

import { EmptyBrowserIcon, WaitIcon } from "../../../../components";
import {
  AiInterviewCallFragment,
  AiInterviewStatus,
  CallStatus,
  useAiInterviewEvaluationQuery,
} from "../../../graphql";
import Evaluation from "../Evaluation/Evaluation";

export type EvaluationTabProps = StyleProps & {
  call: Pick<
    AiInterviewCallFragment,
    | "id"
    | "duration"
    | "isProcessing"
    | "status"
    | "streamableAudio"
    | "endTime"
    | "aiInterview"
  > & { redactedAt?: string | null };
  onPlayMedia?: () => void;
};

const EvaluationTab: React.FC<EvaluationTabProps> = ({
  call,
  onPlayMedia,
  ...props
}) => {
  const { data, loading, error } = useAiInterviewEvaluationQuery({
    variables: { callId: call.id },
    skip: call.isProcessing || call.status === CallStatus.InProgress,
  });

  const aiInterviewEvaluation = data?.aiInterviewEvaluation;

  let notice: {
    message: string;
    description: string;
    MessageIcon: React.ComponentType<any>;
  } | null = null;

  // Show error message if AI interview failed
  if (call.aiInterview?.status === AiInterviewStatus.Error) {
    notice = {
      message: "Interview Failed",
      description:
        "The AI interviewer encountered an error during this interview. The evaluation is not available.",
      MessageIcon: EmptyBrowserIcon,
    };
  } else if (call.isProcessing || call.status === CallStatus.InProgress) {
    if (call.status !== CallStatus.InProgress) {
      notice = {
        message: "We’re still processing this interview",
        description:
          "The transcript and evaluation aren’t available yet while we finish processing and transcribing the recording. This usually takes up to 10 minutes. Please check back then.",
        MessageIcon: EmptyBrowserIcon,
      };
    } else {
      notice = {
        message: "Call is in progress",
        description:
          "When the interview is completed and processed, you can play back the recording and review the evaluation.",
        MessageIcon: EmptyBrowserIcon,
      };
    }
  } else if (call.aiInterview?.isIncomplete) {
    notice = {
      message: "Interview Incomplete",
      description:
        "The interview has not been completed. Complete the interview to view the full evaluation and scores.",
      MessageIcon: WaitIcon,
    };
  } else if (!aiInterviewEvaluation && !loading && !error) {
    notice = {
      message: "Evaluation on the way",
      description:
        "The transcript is ready, but the AI evaluation hasn't been generated yet. It will appear here soon—feel free to refresh or come back in a bit.",
      MessageIcon: WaitIcon,
    };
  }

  if (notice) {
    const { MessageIcon } = notice;
    return (
      <Flex flexDirection="column" align="center" py="10">
        <MessageIcon maxW="240px" />
        <Box
          fontWeight="600"
          color="gray.600"
          fontSize="md"
          textAlign="center"
          marginTop="5"
        >
          {notice.message}
        </Box>
        <Box
          fontSize="sm"
          fontWeight="400"
          color="gray.600"
          mt="2"
          px="6"
          textAlign="center"
        >
          {notice.description}
        </Box>
      </Flex>
    );
  }

  return (
    <Evaluation
      aiInterviewEvaluation={aiInterviewEvaluation}
      callId={call.id}
      callDuration={call.duration}
      loading={loading}
      error={error}
      onPlayMedia={onPlayMedia}
      hasRedaction={
        Boolean(call.redactedAt) ||
        aiInterviewEvaluation?.questions?.some((q) => q.wasAnswerRedacted)
      }
      {...props}
    />
  );
};

export default EvaluationTab;
